/**
 * Cache de thumbnails usando IndexedDB - HostVSL Premium
 * Permite carregamento instantâneo em visitas repetidas
 */

const DB_NAME = 'hostvsl-thumbnail-cache';
const DB_VERSION = 1;
const STORE_NAME = 'thumbnails';
const CACHE_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 dias

interface CachedThumbnail {
  id: string;
  dataUrl: string;
  timestamp: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

const openDB = (): Promise<IDBDatabase> => {
  if (dbPromise) return dbPromise;
  
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
  });
  
  return dbPromise;
};

/**
 * Armazena thumbnail no cache IndexedDB
 */
export const cacheThumbnail = async (id: string, dataUrl: string): Promise<void> => {
  try {
    const db = await openDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    const cached: CachedThumbnail = {
      id,
      dataUrl,
      timestamp: Date.now()
    };
    
    store.put(cached);
  } catch (error) {
    console.warn('Thumbnail cache write error:', error);
  }
};

/**
 * Recupera thumbnail do cache se existir e não estiver expirada
 */
export const getCachedThumbnail = async (id: string): Promise<string | null> => {
  try {
    const db = await openDB();
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    
    return new Promise((resolve) => {
      const request = store.get(id);
      
      request.onsuccess = () => {
        const cached = request.result as CachedThumbnail | undefined;
        
        if (cached && Date.now() - cached.timestamp < CACHE_DURATION_MS) {
          resolve(cached.dataUrl);
        } else {
          resolve(null);
        }
      };
      
      request.onerror = () => resolve(null);
    });
  } catch (error) {
    return null;
  }
};

/**
 * Remove thumbnails expiradas do cache
 */
export const clearExpiredCache = async (): Promise<void> => {
  try {
    const db = await openDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.openCursor();
    
    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
      if (cursor) {
        const cached = cursor.value as CachedThumbnail;
        if (Date.now() - cached.timestamp > CACHE_DURATION_MS) {
          cursor.delete();
        }
        cursor.continue();
      }
    };
  } catch (error) {
    console.warn('Cache cleanup error:', error);
  }
};
